// ============================================================================
// AUDIENCE DIALOG — publishers restrict a Studio TV film or playlist to roles. No roles
// ticked means everyone who can watch Studio TV. A user needs any one of the
// roles (child roles count: Tier 1 is inside Tier 2). Publishers and System
// Administrators always see every film.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { AlertCircle, Check, Loader, Lock, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { StudioTvAudienceRole, listStudioTvRoles } from '../../services/studio-tv';

interface Props {
  initial: StudioTvAudienceRole[] | undefined;
  // What the audience applies to, for the helper text
  subject?: string;
  onClose: () => void;
  // Receives the chosen live role ids; throw to keep the dialog open
  onSave: (roleIds: string[]) => Promise<void>;
}

export default function AudienceDialog({ initial, subject = 'film', onClose, onSave }: Props) {
  const [roles, setRoles] = useState<Array<{ id: string; name: string }> | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set((initial ?? []).map(r => r.id)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listStudioTvRoles().then(setRoles).catch(() => setError('Could not load roles'));
  }, []);

  // A deleted role stays listed (and can be unticked) until saved without it
  const deleted = (initial ?? []).filter(r => r.name === null);

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // Deleted roles are dropped on save: the backend rejects unknown ids
      const live = new Set((roles ?? []).map(r => r.id));
      await onSave(Array.from(selected).filter(id => live.has(id)));
      toast.success(selected.size ? 'Audience restricted' : 'Open to everyone');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save the audience');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden shadow-xl font-display flex flex-col max-h-[85vh]"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
          <div>
            <h2 className="text-lg font-bold text-navy">Who can watch</h2>
            <p className="text-xs text-slate-500">Tick none for everyone. Anyone outside the audience never sees this {subject}.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>

        {error && (
          <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        <div className="p-5 flex flex-col gap-2 overflow-y-auto">
          {!roles ? (
            <div className="flex justify-center py-8 text-slate-400"><Loader size={20} className="animate-spin" /></div>
          ) : (
            <>
              {[...roles, ...deleted.map(r => ({ id: r.id, name: 'Deleted role' }))].map(role => {
                const on = selected.has(role.id);
                return (
                  <button key={role.id} type="button" onClick={() => toggle(role.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                      on ? 'border-primary bg-primary/5' : 'border-[#e2e8f0] hover:border-primary/40 hover:bg-slate-50'
                    }`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      on ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-400'
                    }`}>
                      {on ? <Check size={14} /> : <Users size={14} />}
                    </div>
                    <span className={`text-sm font-semibold ${on ? 'text-navy' : 'text-slate-700'}`}>{role.name}</span>
                  </button>
                );
              })}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
          <span className="flex items-center gap-1.5 text-xs text-slate-500">
            {selected.size ? <><Lock size={12} /> {selected.size} {selected.size === 1 ? 'role' : 'roles'}</> : 'Everyone'}
          </span>
          <div className="flex items-center gap-3">
            <button onClick={onClose}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
              Cancel
            </button>
            <button onClick={save} disabled={saving || !roles}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60">
              {saving ? <Loader size={16} className="animate-spin" /> : <Check size={16} />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
